'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useDebug } from '@/context/debug-context';
import { useTheme } from 'next-themes';

export function useKeyboardShortcuts() {
  const router = useRouter();
  const { showDebug, setShowDebug } = useDebug();
  const { setTheme, theme } = useTheme();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const cmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;
      const isOption = event.altKey;

      if (!event.key) return;

      // All shortcuts require a modifier key to avoid accidental triggers
      if (!isOption && !cmdOrCtrl) return;

      const runShortcut = (action: () => void) => {
        event.preventDefault();
        action();
      };

      switch (event.key.toLowerCase()) {
        // Navigation: Option+1/2/3
        case '1':
          if (isOption) runShortcut(() => router.push('/'));
          break;
        case '2':
          if (isOption) runShortcut(() => router.push('/games'));
          break;
        case '3':
          if (isOption) runShortcut(() => router.push('/partners'));
          break;
        // Game management: Option+G/A/V/S
        case 'g':
          if (isOption) runShortcut(() => router.push('/games/new'));
          break;
        case 'a':
          if (isOption) runShortcut(() => router.push('/games/new'));
          break;
        case 'v':
          if (isOption) {
            runShortcut(() => {
              const voiceButton = document.querySelector('[data-voice-input]') as HTMLElement;
              if (voiceButton) voiceButton.click();
            });
          }
          break;
        case 's':
          if (isOption) {
            runShortcut(() => {
              const searchInput = document.querySelector('[data-search-input]') as HTMLInputElement;
              if (searchInput) searchInput.focus();
            });
          }
          break;
        // Debug & Development: Option+D/T
        case 'd':
          if (isOption) runShortcut(() => setShowDebug(!showDebug));
          break;
        case 't':
          if (isOption) runShortcut(() => setTheme(theme === 'dark' ? 'light' : 'dark'));
          break;
        // General: Option or Cmd/Ctrl for ?// , Option+R for refresh
        case '?':
          if (isOption || cmdOrCtrl) {
            runShortcut(() => {
              const helpButton = document.querySelector('[data-help-menu]') as HTMLElement;
              if (helpButton) helpButton.click();
            });
          }
          break;
        case '/':
          if (isOption || cmdOrCtrl) runShortcut(() => router.push('/docs/shortcuts'));
          break;
        case ',':
          if (isOption) runShortcut(() => router.push('/settings'));
          break;
        case 'r':
          if (isOption) runShortcut(() => window.location.reload());
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [router, showDebug, setShowDebug, setTheme, theme]);
}
